import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { IconButton } from '@material-ui/core';
import { DeleteOutline } from '@material-ui/icons';
import { ICondition, ICriteria, ConjuctionType, Conjuction, RuleElements, ConditionTypes, CriteriaTypes, IConditionData } from '@app/models';
import { Operator, AddElement, ChipSelector, LocalizationSelector, TimeSelector, IPSelector, AttributeSelector } from '.';
import { PasslySelect, PasslyToggle } from '@app/components';
import { createConditionTypes } from '../methods';
import { addCondition, removeCondition, updateCondition } from '@app/store/actions';

interface IProps {
    condition: ICondition;
    index: number;
    ruleID?: string;
    isLast: boolean;
    operator: ConjuctionType;
    conditionOperator: Function;
    validation: Function;
    patchData: IConditionData | null;
}

interface IConditionSelection {
    conditionElement: any;
    policyElement: ConditionTypes | '';
    criteria: CriteriaTypes | '';
    criteriaElement: ICriteria | null | undefined;
}

export const Condition = (props: IProps) => {
    const { condition, conditionOperator, index, ruleID, isLast, operator, validation, patchData } = props;
    const dispatch = useDispatch();

    const [conditionSelection, setConditionSelection] = useState<IConditionSelection>(
        {
            conditionElement: null,
            policyElement: '',
            criteria: '',
            criteriaElement: null
        }
    );
    const [criteriaList, setCriteriaList] = useState<ICriteria[]>([]);
    const [isValid, setIsValid] = useState<boolean>(false);
    const [isFirst, setIsFirst] = useState<boolean>(false);

    const conditionTypes = createConditionTypes();

    useEffect(() => {
        if (patchData !== null) {
            if (patchData.type === ConditionTypes.ipConf && (patchData.property === CriteriaTypes.forwardinrange || patchData.property === CriteriaTypes.forwardnotinrange)) {
                patchData.type = ConditionTypes.forwardIP;
            } else if (patchData.type === ConditionTypes.ipConf) {
                patchData.type = ConditionTypes.ip;
            }

            const patchedCondition = findConditionElement(patchData.type);
            if (patchedCondition !== undefined) setCriteriaList(patchedCondition.properties);
            const patchedCriteria = findCriteriaElement(patchData.property, patchedCondition.properties);

            setConditionSelection({
                conditionElement: patchedCondition,
                policyElement: patchData.type,
                criteria: patchData.property,
                criteriaElement: patchedCriteria
            });
        }
    }, []);

    useEffect(() => {
        index === 0
            ? setIsFirst(true)
            : setIsFirst(false);
    }, [index]);

    useEffect(() => {
        validation(isValid, RuleElements.condition);
    }, [isValid]);

    useEffect(() => {
        if (conditionSelection.criteria === CriteriaTypes.signin) getData(true, []);
        if (conditionSelection.criteria === CriteriaTypes.trusted || conditionSelection.criteria === CriteriaTypes.untrusted) getData(true, null);
        if (isValid && conditionSelection.policyElement !== '' && conditionSelection.criteria !== '' && condition.object?.filter) {
            dispatch(updateCondition(condition.id, condition.object.filter, conditionSelection.policyElement, conditionSelection.criteria, ruleID));
        }
    }, [conditionSelection.criteria]);

    const isBoolSelector = (index: number): boolean => {
        return index === 1;
    };

    const handlePolicyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value as ConditionTypes;
        const selectedCondition = findConditionElement(value);

        setIsValid(false);
        setConditionSelection({ conditionElement: selectedCondition, policyElement: value, criteria: '', criteriaElement: null });
        if (selectedCondition !== undefined) {
            setCriteriaList(selectedCondition.properties);
        }
    };

    const handleCriteriaChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value as CriteriaTypes;
        const selectedCriteria = findCriteriaElement(value);
        if (selectedCriteria !== null && selectedCriteria !== undefined) {
            setConditionSelection(prevState => ({ ...prevState, criteria: value, criteriaElement: selectedCriteria }));
        }
    };

    const handleOperatorCallback = (operator: ConjuctionType) => {
        conditionOperator(operator);
    };

    const findConditionElement = (value: ConditionTypes): any | undefined => {
        return conditionTypes.find((condition: any) => condition.value === value);
    };

    const findCriteriaElement = (value: CriteriaTypes, list?: ICriteria[]): ICriteria | undefined => {
        const searchList: ICriteria[] = list ?? criteriaList;
        return searchList.find((criteria: ICriteria) => criteria.value === value);
    };

    const getData = (valid: boolean, received: any) => {
        setIsValid(valid);
        if (valid && conditionSelection.policyElement !== '' && conditionSelection.criteria !== '') {
            dispatch(updateCondition(condition.id, received, conditionSelection.policyElement, conditionSelection.criteria, ruleID));
        }
    };

    const getIcon = (item: any) => {
        return <div className='condition__select-icon'>{item.icon}</div>;
    };

    return (
        <div className='condition'>
            <div className='condition__container'>
                <div className='condition__selection'>
                    <PasslySelect
                        id='conditionSelect'
                        label={'AUTH_POLICY_OBJ_LABEL'}
                        value={conditionSelection.policyElement}
                        onChange={handlePolicyChange}
                        options={conditionTypes}
                        icon={getIcon}
                    />
                </div>
                <div className='condition__criteria'>
                    {conditionSelection.policyElement !== '' && conditionSelection.conditionElement !== null &&
                        <>
                            <div className='condition__criteria-select'>
                                <PasslySelect
                                    id='conditionCriteriaSelect'
                                    label={'AUTH_POLICY_OBJ_PROP_LABEL'}
                                    value={conditionSelection.criteria}
                                    onChange={handleCriteriaChange}
                                    options={conditionSelection.conditionElement.properties}
                                />
                            </div>
                            <div className='condition__criteria-form'>
                                {conditionSelection.criteriaElement?.chip &&
                                    <ChipSelector
                                        checkValidation={getData}
                                        type={conditionSelection.criteriaElement.chip}
                                        label={conditionSelection.criteriaElement.chipName}
                                        patchData={patchData?.filter}
                                    />
                                }
                                {conditionSelection.criteriaElement?.list &&
                                    <LocalizationSelector
                                        checkValidation={getData}
                                        criteria={conditionSelection.criteriaElement}
                                        patchData={patchData?.filter}
                                    />
                                }
                                {conditionSelection.criteriaElement?.time &&
                                    <TimeSelector checkValidation={getData} patchData={patchData?.filter} />
                                }
                                {conditionSelection.criteriaElement?.ip &&
                                    <IPSelector checkValidation={getData} patchData={patchData?.filter} />
                                }
                                {conditionSelection.criteriaElement?.attribute &&
                                    <AttributeSelector checkValidation={getData} patchData={patchData?.filter} />
                                }
                            </div>
                        </>
                    }
                </div>
                <div className='condition__delete'>
                    {!isFirst &&
                        <IconButton
                            id='deleteCondition'
                            size='small'
                            onClick={() => dispatch(removeCondition(condition.id, ruleID))}
                        >
                            <DeleteOutline />
                        </IconButton>
                    }
                </div>
            </div>

            <div className='condition__aside'>
                <div className='condition__statement'>
                    {index > 0
                        ? !isBoolSelector(index)
                            ? <Operator text={operator} />
                            : <PasslyToggle first={Conjuction.and} second={Conjuction.or} onChoose={handleOperatorCallback} chosen={operator ? operator : ''} />
                        : <></>
                    }
                </div>
                {isLast &&
                    <div className='condition__add'>
                        <AddElement
                            disabled={!isValid}
                            onClick={() => dispatch(addCondition(null, ruleID))}
                        />
                    </div>
                }
            </div>

        </div>
    );
};
